import { z } from 'zod';

export const AuditDiffSchema = z.record(
  z.string(),
  z.object({ before: z.unknown(), after: z.unknown() }),
);
export type AuditDiff = z.infer<typeof AuditDiffSchema>;

export const NarratedChangeSchema = z.object({
  field: z.string(),
  label: z.string(),
  before: z.unknown(),
  after: z.unknown(),
  display: z.enum(['text', 'enum', 'json', 'date', 'boolean', 'number', 'reference']),
  // For enum/reference, the resolved human label for each side. References that
  // didn't resolve (e.g. a deleted referent) keep a null display and the UI
  // falls back to the raw id.
  beforeDisplay: z.string().nullable().optional(),
  afterDisplay: z.string().nullable().optional(),
  // For 'reference', the entity type the id points to.
  referenceEntityType: z.string().optional(),
});
export type NarratedChange = z.infer<typeof NarratedChangeSchema>;

export const AuditOperationSchema = z.enum(['create', 'update', 'delete', 'restore']);
export type AuditOperation = z.infer<typeof AuditOperationSchema>;

export const AuditSourceSchema = z.enum(['ui', 'import', 'api', 'system']);
export type AuditSource = z.infer<typeof AuditSourceSchema>;

export const AuditEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).transform((v) => String(v)),
  actorId: z.string().nullable(),
  /** Assumed-user id when the write happened under impersonation. */
  impersonatedUserId: z.string().nullable(),
  /** Where the write originated (ui / import / api / system). */
  source: AuditSourceSchema,
  /** Domain the audited entity belongs to (e.g. 'customers'). */
  entityType: z.string(),
  /** Id of the audited entity within its domain. */
  entityId: z.string(),
  operation: AuditOperationSchema,
  version: z.number().int().nullable(),
  occurredAt: z.string(),
  summary: z.string(),
  changes: z.array(NarratedChangeSchema),
  raw: AuditDiffSchema,
  /** The editor's answer to "When did this become true?" on an employee or
   *  customer edit: `current_version` corrected the version in effect (logged
   *  timesheets now show the new values), `today` started a new version.
   *  `version` is the version written. Null when the edit asked nothing. */
  appliedTo: z
    .object({
      answer: z.enum(['today', 'current_version']),
      version: z.number().int().nullable(),
    })
    .nullable()
    .optional(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

/**
 * A platform-listing audit row: an {@link AuditEntry} enriched with the
 * entity's identifying label and the actor / impersonated-user display names.
 * Each label is `null` (never blank) when the id couldn't be resolved — a
 * hard-deleted entity, or an entity type without a label resolver — so the UI
 * renders an explicit fallback rather than an empty cell.
 */
export const AuditListItemSchema = AuditEntrySchema.extend({
  label: z.string().nullable(),
  actorLabel: z.string().nullable(),
  impersonatedUserLabel: z.string().nullable(),
});
export type AuditListItem = z.infer<typeof AuditListItemSchema>;

/**
 * Query for the unified audit endpoint. Every filter is optional, so the same
 * call serves the platform-wide screen (no entity filter) and a per-object
 * embed (`entityType` + `entityId`). `from`/`to` are ISO timestamps bounding
 * `occurredAt`; `actorId` filters by the employee who made the change.
 */
export interface ListAuditQuery {
  entityType?: string;
  entityId?: string;
  /**
   * Roll-up mode: a parent entity type + id whose tab shows the parent's own
   * history plus every child scoped to it (a customer's team/contacts/SOWs, an
   * employee's cost). Mutually exclusive with `entityType`/`entityId`.
   */
  scopeType?: string;
  scopeId?: string;
  actorId?: string;
  operation?: AuditOperation;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}
